import {
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { nanoid } from 'nanoid'
import { useNavigate } from "react-router-dom";
import { useState } from "react";

import {
    CursorPointer,
    EligibilityHeadCell,
    EligibiliyBodyCell,
    NoDataCell,
    NoDataSubtitle,
    PatientContainerBox,
    PatientDataTableBody,
    PatientTableCell,
    PatientTableHeadCell,
    RecordListRow,
    TableNameDetails,
    ViewButton
} from "../../styles/styles";
import { PatientHeaderLabel } from "../../../../constants/AllPatientRecords";
import fontWeight from "../../../../styles/mui/fontWeight";
import { colors } from "../../../../styles/colors";
import { AllPatientRecord, PatientRecordSearch } from "../../../../models/Patient";
import { DateFormats } from "../../../../constants/Date";
import { length, getFormattedName, encryptId } from "../../../../utils";
import { formatDate } from "../../../../utils/DateTime";
import CustomTablePagination from "../../../../App/components/CustomTablePagination";
import { PaginationPayload, SearchPayloadForRecords } from "../../../../models/Authorization";
import { DefaultPaginationData } from "../../../../constants/Disciplines";

export interface PropsFromState {
    patientRecord: PatientRecordSearch | null;
    searchPayload: SearchPayloadForRecords;
}

export interface PropsFromDispatch {
    postAllPatientRecord: (payload: SearchPayloadForRecords) => void;
}

type Props = PropsFromState & PropsFromDispatch;

const SearchRecords: React.FC<Props> = ({ patientRecord, postAllPatientRecord, searchPayload }: Props) => {
    const [paginationData, setPaginationData] = useState<PaginationPayload>(DefaultPaginationData);

    const navigate = useNavigate();

    const handlePagination = (paginationData: PaginationPayload) => {
        const payload = {
            ...paginationData,
            dateOfBirth: searchPayload.dateOfBirth,
            lastName: searchPayload.lastName,
            subscriberId: searchPayload.subscriberId,
        };
        setPaginationData(paginationData);
        postAllPatientRecord(payload);
    };

    const handleView = async (patientId: number) => {
        const encryptedPatientId = await encryptId(patientId);
        navigate(`/patient-details/${encryptedPatientId}`);
    };

    const hasPatientData = !!patientRecord?.patientResponse && length(patientRecord.patientResponse) &&
        patientRecord.patientResponse[0].patientId !== 0;

    return (
        <Grid container>
            <Grid size={12}>
                <PatientContainerBox>
                    <TableContainer
                        component={Paper}
                        sx={{ p: "1.2rem 1rem 1rem 1.2rem" }}
                    >
                        <Table stickyHeader aria-label="sticky table">
                            {hasPatientData && (<TableHead>
                                <TableRow>
                                    {PatientHeaderLabel.map((tablecell) => {
                                        return (
                                            <TableCell
                                                key={tablecell.label}
                                                sx={
                                                    tablecell.label == "ELIGIBILITY DATES"
                                                        ? EligibilityHeadCell
                                                        : PatientTableHeadCell
                                                }
                                                width={tablecell.width}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={fontWeight.Weight[3]}
                                                    color={colors.black[2]}
                                                >
                                                    {tablecell.label}
                                                </Typography>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>)}
                            <TableBody sx={PatientDataTableBody}>
                                {patientRecord &&
                                    patientRecord.patientResponse &&
                                    length(patientRecord.patientResponse) &&
                                    patientRecord.patientResponse[0].patientId !== 0 ? (
                                    patientRecord.patientResponse.map(
                                        (row: AllPatientRecord, rowIndex: number) => {
                                            return (
                                                <TableRow key={nanoid()} sx={RecordListRow}>
                                                    <TableCell
                                                        sx={TableNameDetails(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h4"
                                                            fontWeight={fontWeight.Weight[4]}
                                                            color={colors.fonts[18]}
                                                            sx={CursorPointer}
                                                            onClick={() => void handleView(row.patientId).catch(() => {})}
                                                            data-testid={`patient-name-${row.fullName}`}
                                                        >
                                                            {getFormattedName(row.fullName)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={PatientTableCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={fontWeight.Weight[4]}
                                                            color={colors.fonts[1]}
                                                        >
                                                            {formatDate(
                                                                row.dateOfBirth,
                                                                DateFormats.MM_DD_YYYY,
                                                                true
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={PatientTableCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={fontWeight.Weight[4]}
                                                            color={colors.fonts[1]}
                                                        >
                                                            {row.subscriberId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={PatientTableCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={fontWeight.Weight[4]}
                                                            color={colors.fonts[1]}
                                                        >
                                                            {row.medicareBeneficiaryIdentifier}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={EligibiliyBodyCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight={fontWeight.Weight[2]}
                                                            color={colors.fonts[1]}
                                                        >
                                                            {row.eligibilityDates}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={PatientTableCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={fontWeight.Weight[2]}
                                                            color={colors.fonts[1]}
                                                        >
                                                            {row.streetName1}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={PatientTableCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={fontWeight.Weight[2]}
                                                            color={colors.fonts[1]}
                                                        >
                                                            {row.city}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={PatientTableCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={fontWeight.Weight[2]}
                                                            color={colors.fonts[1]}
                                                        >
                                                            {row.patientState}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={PatientTableCell(
                                                            patientRecord.patientResponse.length - 1 ===
                                                            rowIndex
                                                        )}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            sx={ViewButton}
                                                            onClick={() => void handleView(row.patientId).catch(() => { })}
                                                            data-testid="view-patient-details"
                                                        >
                                                            VIEW
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }
                                    )
                                ) : (
                                        <TableRow>
                                            <TableCell
                                                align="center"
                                                colSpan={9}
                                                sx={NoDataCell}
                                            >
                                                <Typography
                                                    sx={NoDataSubtitle}
                                                >
                                                    No matching members found. Please verify search criteria and try again.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </PatientContainerBox>
            </Grid>
            {hasPatientData && (<Grid size={12} m={"0.5rem"}>
                <CustomTablePagination
                    totalRecords={
                        patientRecord && patientRecord?.totalRows ? patientRecord?.totalRows : 0
                    }
                    handlePagination={handlePagination}
                    paginationData={paginationData}
                    paginationInjectedTo="patient-records"
                />
            </Grid>
            )}
        </Grid>
    )
}

export default SearchRecords;